import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthService } from '../lib/services/AuthService';
import { Mail, Lock, Eye, EyeOff, AlertCircle, CheckCircle2, Loader2, ArrowLeft } from 'lucide-react';
import { EMAIL_CONFIG } from '../config/constants';
import Logo from '../logo.svg';

type Step = 'email' | 'code' | 'password';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);

    if (!email) {
      setError('Veuillez entrer votre adresse email');
      return;
    }

    try {
      setLoading(true);
      const { error: sendError } = await AuthService.sendResetCode(email);
      
      if (sendError) {
        // Vérifier si c'est une erreur "utilisateur non trouvé"
        if (sendError.includes("Aucun compte") || sendError.includes("USER_NOT_FOUND")) {
          setError('Aucun compte n\'est associé à cette adresse email. Vérifiez votre saisie ou créez un compte.');
        } else {
          setError(sendError);
        }
        return;
      }

      setSuccessMsg('Code envoyé par email. Vérifiez votre boîte de réception.');
      setStep('code');
    } catch (err: any) {
      // Gérer les erreurs HTTP 404 (utilisateur non trouvé)
      if (err.message?.includes('404') || err.message?.includes("Aucun compte")) {
        setError('Aucun compte n\'est associé à cette adresse email. Vérifiez votre saisie ou créez un compte.');
      } else {
        setError(err.message || 'Erreur lors de l\'envoi du code');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);

    if (!code || code.length !== 6) {
      setError('Veuillez entrer le code à 6 chiffres reçu par email');
      return;
    }

    try {
      setLoading(true);
      const { error: verifyError } = await AuthService.verifyResetCode(email, code);
      
      if (verifyError) {
        setError(verifyError);
        return;
      }

      setSuccessMsg('Code vérifié avec succès');
      setStep('password');
    } catch (err: any) {
      setError(err.message || 'Code invalide ou expiré');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);

    if (!newPassword || !confirmPassword) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    if (newPassword.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    try {
      setLoading(true);
      const { error: resetError } = await AuthService.resetPasswordWithCode(email, newPassword);
      
      if (resetError) {
        setError(resetError);
        return;
      }

      setSuccessMsg('Mot de passe réinitialisé avec succès. Redirection vers la page de connexion...');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la réinitialisation du mot de passe');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setCode('');
    setError(null);
    setSuccessMsg(null);
  };

  const handleBackToCode = () => {
    setStep('code');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
    setSuccessMsg(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
            <img src={Logo} alt="Logo" className="w-20 h-20" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">
            Mot de passe oublié
          </h2>
          <p className="text-dark-400">
            {step === 'email' && 'Entrez votre adresse email pour recevoir un code de réinitialisation'}
            {step === 'code' && 'Entrez le code reçu par email'}
            {step === 'password' && 'Définissez votre nouveau mot de passe'}
          </p>
        </div>

        <div className="bg-dark-800 rounded-lg p-6 shadow-xl">
          {/* Barre de progression */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <div className={`flex-1 h-2 rounded-full ${step !== 'email' ? 'bg-primary-500' : 'bg-dark-600'}`}></div>
              <div className={`w-2 h-2 rounded-full mx-2 ${step !== 'email' ? 'bg-primary-500' : 'bg-dark-600'}`}></div>
              <div className={`flex-1 h-2 rounded-full ${step === 'code' ? 'bg-primary-500' : step === 'password' ? 'bg-primary-500' : 'bg-dark-600'}`}></div>
              <div className={`w-2 h-2 rounded-full mx-2 ${step === 'password' ? 'bg-primary-500' : 'bg-dark-600'}`}></div>
              <div className={`flex-1 h-2 rounded-full ${step === 'password' ? 'bg-primary-500' : 'bg-dark-600'}`}></div>
            </div>
            <div className="flex justify-between text-xs text-dark-400">
              <span className={step === 'email' ? 'text-primary-400' : ''}>Email</span>
              <span className={step === 'code' ? 'text-primary-400' : ''}>Code</span>
              <span className={step === 'password' ? 'text-primary-400' : ''}>Mot de passe</span>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-lg flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <span className="text-red-400 text-sm">{error}</span>
            </div>
          )}

          {successMsg && (
            <div className="mb-4 p-3 bg-green-500/20 border border-green-500 rounded-lg flex items-center space-x-2">
              <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
              <span className="text-green-400 text-sm">{successMsg}</span>
            </div>
          )}

          {/* Étape 1 : Email */}
          {step === 'email' && (
            <form onSubmit={handleSendCode} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-dark-400 mb-2">
                  Adresse email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-dark-500" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-3 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={EMAIL_CONFIG.PLACEHOLDER}
                    disabled={loading}
                    autoFocus
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full px-4 py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-lg transition-colors font-medium flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Envoi en cours...</span>
                  </>
                ) : (
                  <span>Envoyer le code</span>
                )}
              </button>
            </form>
          )}

          {/* Étape 2 : Code */}
          {step === 'code' && (
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-dark-400 mb-2">
                  Code de réinitialisation
                </label>
                <input
                  id="code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-center text-2xl tracking-widest"
                  placeholder="000000"
                  disabled={loading}
                  maxLength={6}
                  autoFocus
                />
                <p className="mt-2 text-xs text-dark-500 text-center">
                  Entrez le code à 6 chiffres reçu par email
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleBackToEmail}
                  disabled={loading}
                  className="flex-1 px-4 py-3 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors font-medium flex items-center justify-center space-x-2"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span>Retour</span>
                </button>
                <button
                  type="submit"
                  disabled={loading || code.length !== 6}
                  className="flex-1 px-4 py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-lg transition-colors font-medium flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Vérification...</span>
                    </>
                  ) : (
                    <span>Vérifier</span>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Étape 3 : Nouveau mot de passe */}
          {step === 'password' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-dark-400 mb-2">
                  Nouveau mot de passe
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-dark-500" />
                  </div>
                  <input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Votre nouveau mot de passe"
                    disabled={loading}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-dark-400 hover:text-white transition-colors"
                    disabled={loading}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-dark-400 mb-2">
                  Confirmer le mot de passe
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-dark-500" />
                  </div>
                  <input
                    id="confirmPassword"
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Confirmez le mot de passe"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-dark-400 hover:text-white transition-colors"
                    disabled={loading}
                  >
                    {showConfirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleBackToCode}
                  disabled={loading}
                  className="flex-1 px-4 py-3 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors font-medium flex items-center justify-center space-x-2"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span>Retour</span>
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-lg transition-colors font-medium flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Réinitialisation...</span>
                    </>
                  ) : (
                    <span>Réinitialiser</span>
                  )}
                </button>
              </div>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
            >
              Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;

